import { useCallback, useEffect, useMemo, useState } from "react";
import { translations } from "./translations";

const LANGUAGE_KEY = "hookora_language";
const DEFAULT_LANGUAGE = "en";

const getStoredLanguage = () => {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  return localStorage.getItem(LANGUAGE_KEY) || DEFAULT_LANGUAGE;
};

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;

  return path.split(".").reduce((acc, key) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
      return acc[key];
    }
    return undefined;
  }, obj);
};

const interpolate = (text, params = {}) => {
  if (typeof text !== "string") return text;

  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return params[key] ?? `{{${key}}}`;
  });
};

const useI18n = () => {
  const [language, setLanguageState] = useState(getStoredLanguage());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncLanguage = () => {
      setLanguageState(localStorage.getItem(LANGUAGE_KEY) || DEFAULT_LANGUAGE);
    };

    window.addEventListener("storage", syncLanguage);
    window.addEventListener("hookora-language-change", syncLanguage);

    return () => {
      window.removeEventListener("storage", syncLanguage);
      window.removeEventListener("hookora-language-change", syncLanguage);
    };
  }, []);

  const setLanguage = useCallback((nextLanguage) => {
    const safeLanguage = translations[nextLanguage] ? nextLanguage : DEFAULT_LANGUAGE;

    setLanguageState(safeLanguage);

    if (typeof window !== "undefined") {
      localStorage.setItem(LANGUAGE_KEY, safeLanguage);
      window.dispatchEvent(new CustomEvent("hookora-language-change"));
    }
  }, []);

  const t = useCallback(
    (key, params = {}) => {
      const activeTranslations = translations[language] || translations[DEFAULT_LANGUAGE];
      const fallbackTranslations = translations[DEFAULT_LANGUAGE];

      const value =
        getNestedValue(activeTranslations, key) ??
        getNestedValue(fallbackTranslations, key);

      if (value === undefined) {
        return key;
      }

      if (typeof value === "string") {
        return interpolate(value, params);
      }

      return value;
    },
    [language]
  );

  return useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, setLanguage, t]
  );
};

export default useI18n;